import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { 
  GET_WALKTHROUGH_HOURS_THRESHOLD, 
  SET_WALKTHROUGH_HOURS_THRESHOLD 
} from '../../graphql/settings';

const GET_RESERVATION_SYNC_INTERVAL = gql`
  query GetReservationSyncInterval {
    reservationSyncInterval
  }
`;

const SET_RESERVATION_SYNC_INTERVAL = gql`
  mutation SetReservationSyncInterval($hours: Int!) {
    setReservationSyncInterval(hours: $hours)
  }
`;

const GET_RESERVATION_AUTO_SYNC_ENABLED = gql`
  query GetReservationAutoSyncEnabled {
    isReservationAutoSyncEnabled
  }
`;

const SET_RESERVATION_AUTO_SYNC_ENABLED = gql`
  mutation SetReservationAutoSyncEnabled($enabled: Boolean!) {
    setReservationAutoSyncEnabled(enabled: $enabled)
  }
`;

const GET_RESERVATION_SYNC_LAST_RUN = gql`
  query GetReservationSyncLastRun {
    reservationSyncLastRun
  }
`;

const GET_PROPERTY_SYNC_INTERVAL = gql`
  query GetPropertySyncInterval {
    propertySyncInterval
  }
`;

const SET_PROPERTY_SYNC_INTERVAL = gql`
  mutation SetPropertySyncInterval($hours: Int!) {
    setPropertySyncInterval(hours: $hours)
  }
`;

const GET_PROPERTY_AUTO_SYNC_ENABLED = gql`
  query GetPropertyAutoSyncEnabled {
    isPropertyAutoSyncEnabled
  }
`;

const SET_PROPERTY_AUTO_SYNC_ENABLED = gql`
  mutation SetPropertyAutoSyncEnabled($enabled: Boolean!) {
    setPropertyAutoSyncEnabled(enabled: $enabled)
  }
`;

const GET_PROPERTY_SYNC_LAST_RUN = gql`
  query GetPropertySyncLastRun {
    propertySyncLastRun
  }
`;

const GET_WORK_ORDER_SYNC_INTERVAL = gql`
  query GetWorkOrderSyncInterval {
    workOrderSyncInterval
  }
`;

const SET_WORK_ORDER_SYNC_INTERVAL = gql`
  mutation SetWorkOrderSyncInterval($minutes: Int!) {
    setWorkOrderSyncInterval(minutes: $minutes)
  }
`;

const GET_WORK_ORDER_AUTO_SYNC_ENABLED = gql`
  query GetWorkOrderAutoSyncEnabled {
    isWorkOrderAutoSyncEnabled
  }
`;

const SET_WORK_ORDER_AUTO_SYNC_ENABLED = gql`
  mutation SetWorkOrderAutoSyncEnabled($enabled: Boolean!) {
    setWorkOrderAutoSyncEnabled(enabled: $enabled)
  }
`;

const GET_WORK_ORDER_SYNC_LAST_RUN = gql`
  query GetWorkOrderSyncLastRun {
    workOrderSyncLastRun
  }
`;

const GET_REVIEW_SYNC_SETTINGS = gql`
  query GetReviewSyncSettings {
    reviewSyncInterval
    isReviewAutoSyncEnabled
    reviewSyncLastRun
    reviewSyncLastSuccess
    reviewSyncLastError
  }
`;

const SET_REVIEW_SYNC_INTERVAL = gql`
  mutation SetReviewSyncInterval($hours: Int!) {
    setReviewSyncInterval(hours: $hours)
  }
`;

const SET_REVIEW_AUTO_SYNC_ENABLED = gql`
  mutation SetReviewAutoSyncEnabled($enabled: Boolean!) {
    setReviewAutoSyncEnabled(enabled: $enabled)
  }
`;



function JobSettingsSection() {
  // Job Settings state
  const [walkthroughHoursThreshold, setWalkthroughHoursThreshold] = useState(72);
  const [isEditingHours, setIsEditingHours] = useState(false);
  const [reservationSyncInterval, setReservationSyncInterval] = useState(4); // Default 4 hours
  const [isEditingSyncInterval, setIsEditingSyncInterval] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true); // Default to enabled
  const [propertySyncInterval, setPropertySyncInterval] = useState(24); // Default 24 hours
  const [isEditingPropertySyncInterval, setIsEditingPropertySyncInterval] = useState(false);
  const [propertyAutoSyncEnabled, setPropertyAutoSyncEnabled] = useState(true); // Default to enabled

  // Work order sync state
  const [workOrderSyncInterval, setWorkOrderSyncInterval] = useState(360); // Default 360 minutes
  const [isEditingWorkOrderSyncInterval, setIsEditingWorkOrderSyncInterval] = useState(false);
  const [workOrderAutoSyncEnabled, setWorkOrderAutoSyncEnabled] = useState(true); // Default to enabled
  const [reviewSyncInterval, setReviewSyncInterval] = useState(24);
  const [isEditingReviewSyncInterval, setIsEditingReviewSyncInterval] = useState(false);
  const [reviewAutoSyncEnabled, setReviewAutoSyncEnabled] = useState(true);



  // Get walkthrough hours threshold
  const { 
    data: hoursThresholdData, 
    loading: hoursThresholdLoading 
  } = useQuery(GET_WALKTHROUGH_HOURS_THRESHOLD);

  // Get reservation sync interval
  const { data: syncIntervalData, loading: syncIntervalLoading } = useQuery(GET_RESERVATION_SYNC_INTERVAL);
  
  // Get auto sync enabled status
  const { data: autoSyncEnabledData } = useQuery(GET_RESERVATION_AUTO_SYNC_ENABLED);

  // Get last sync timestamp
  const { data: lastSyncData } = useQuery(GET_RESERVATION_SYNC_LAST_RUN);

  // Get property sync interval
  const { data: propertySyncIntervalData, loading: propertySyncIntervalLoading } = useQuery(GET_PROPERTY_SYNC_INTERVAL);
  
  // Get property auto sync enabled status
  const { data: propertyAutoSyncEnabledData } = useQuery(GET_PROPERTY_AUTO_SYNC_ENABLED);

  // Get property sync last run timestamp
  const { data: propertyLastSyncData } = useQuery(GET_PROPERTY_SYNC_LAST_RUN);

  // Get work order sync interval
  const { data: workOrderSyncIntervalData, loading: workOrderSyncIntervalLoading } = useQuery(GET_WORK_ORDER_SYNC_INTERVAL);
  
  // Get work order auto sync enabled status
  const { data: workOrderAutoSyncEnabledData } = useQuery(GET_WORK_ORDER_AUTO_SYNC_ENABLED);

  // Get work order sync last run timestamp
  const { data: workOrderLastSyncData } = useQuery(GET_WORK_ORDER_SYNC_LAST_RUN);
  const { data: reviewSyncSettingsData, loading: reviewSyncSettingsLoading } = useQuery(GET_REVIEW_SYNC_SETTINGS);



  // Set walkthrough hours threshold mutation
  const [updateWalkthroughHours, { loading: updatingHours }] = useMutation(SET_WALKTHROUGH_HOURS_THRESHOLD, {
    onCompleted: () => {
      toast.success('Walkthrough hours threshold updated');
      setIsEditingHours(false);
    },
    onError: (error) => {
      toast.error('Failed to update walkthrough hours threshold');
      console.error(error);
    }
  });

  // Set reservation sync interval mutation
  const [updateReservationSyncInterval, { loading: updatingInterval }] = useMutation(SET_RESERVATION_SYNC_INTERVAL, {
    onCompleted: () => {
      toast.success('Reservation sync interval updated');
      setIsEditingSyncInterval(false);
    },
    onError: (error) => {
      toast.error('Failed to update reservation sync interval');
      console.error(error);
    }
  });
  
  // Set auto sync enabled mutation
  const [updateAutoSyncEnabled, { loading: updatingAutoSync }] = useMutation(SET_RESERVATION_AUTO_SYNC_ENABLED, {
    onCompleted: (data) => {
      toast.success(`Automatic reservation syncing ${autoSyncEnabled ? 'enabled' : 'disabled'}`);
    },
    onError: (error) => {
      console.error('Failed to update automatic sync setting:', error);
      toast.error('Failed to update automatic sync setting');
      // Revert the toggle if there was an error
      setAutoSyncEnabled(!autoSyncEnabled);
    }
  });
  
  // Set property sync interval mutation
  const [updatePropertySyncInterval, { loading: updatingPropertyInterval }] = useMutation(SET_PROPERTY_SYNC_INTERVAL, {
    onCompleted: () => {
      toast.success('Property sync interval updated');
      setIsEditingPropertySyncInterval(false);
    },
    onError: (error) => {
      toast.error('Failed to update property sync interval');
      console.error(error);
    }
  });
  
  // Set property auto sync enabled mutation
  const [updatePropertyAutoSyncEnabled, { loading: updatingPropertyAutoSync }] = useMutation(SET_PROPERTY_AUTO_SYNC_ENABLED, {
    onCompleted: (data) => {
      toast.success(`Automatic property syncing ${propertyAutoSyncEnabled ? 'enabled' : 'disabled'}`);
    },
    onError: (error) => {
      console.error('Failed to update property automatic sync setting:', error);
      toast.error('Failed to update property automatic sync setting');
      // Revert the toggle if there was an error
      setPropertyAutoSyncEnabled(!propertyAutoSyncEnabled);
    }
  });
  
  // Set work order sync interval mutation
  const [updateWorkOrderSyncInterval, { loading: updatingWorkOrderInterval }] = useMutation(SET_WORK_ORDER_SYNC_INTERVAL, {
    onCompleted: () => {
      toast.success('Work order sync interval updated');
      setIsEditingWorkOrderSyncInterval(false);
    },
    onError: (error) => {
      toast.error('Failed to update work order sync interval');
      console.error(error);
    }
  });
  
  // Set work order auto sync enabled mutation
  const [updateWorkOrderAutoSyncEnabled, { loading: updatingWorkOrderAutoSync }] = useMutation(SET_WORK_ORDER_AUTO_SYNC_ENABLED, {
    onCompleted: (data) => {
      toast.success(`Automatic work order syncing ${workOrderAutoSyncEnabled ? 'enabled' : 'disabled'}`);
    },
    onError: (error) => {
      console.error('Failed to update work order automatic sync setting:', error);
      toast.error('Failed to update work order automatic sync setting');
      // Revert the toggle if there was an error
      setWorkOrderAutoSyncEnabled(!workOrderAutoSyncEnabled);
    }
  });

  const [updateReviewSyncInterval, { loading: updatingReviewInterval }] = useMutation(SET_REVIEW_SYNC_INTERVAL, {
    onCompleted: () => {
      toast.success('Review sync interval updated');
      setIsEditingReviewSyncInterval(false);
    },
    onError: () => toast.error('Failed to update review sync interval')
  });

  const [updateReviewAutoSyncEnabled, { loading: updatingReviewAutoSync }] = useMutation(SET_REVIEW_AUTO_SYNC_ENABLED, {
    onError: () => {
      setReviewAutoSyncEnabled(value => !value);
      toast.error('Failed to update automatic review syncing');
    }
  });





  // Update local state when data is loaded
  useEffect(() => {
    if (hoursThresholdData?.walkthroughHoursThreshold) {
      setWalkthroughHoursThreshold(hoursThresholdData.walkthroughHoursThreshold);
    }
  }, [hoursThresholdData]);

  // Update local state when data is loaded
  useEffect(() => {
    if (syncIntervalData?.reservationSyncInterval) {
      setReservationSyncInterval(syncIntervalData.reservationSyncInterval);
    }
  }, [syncIntervalData]);
  
  // Update local state when auto sync enabled status is loaded
  useEffect(() => {
    if (autoSyncEnabledData?.isReservationAutoSyncEnabled !== undefined) {
      setAutoSyncEnabled(autoSyncEnabledData.isReservationAutoSyncEnabled);
    }
  }, [autoSyncEnabledData]);
  
  // Update local state when property sync data is loaded
  useEffect(() => {
    if (propertySyncIntervalData?.propertySyncInterval) {
      setPropertySyncInterval(propertySyncIntervalData.propertySyncInterval);
    }
  }, [propertySyncIntervalData]);
  
  // Update local state when property auto sync enabled status is loaded
  useEffect(() => {
    if (propertyAutoSyncEnabledData?.isPropertyAutoSyncEnabled !== undefined) {
      setPropertyAutoSyncEnabled(propertyAutoSyncEnabledData.isPropertyAutoSyncEnabled);
    }
  }, [propertyAutoSyncEnabledData]);
  
  // Update local state when work order sync data is loaded
  useEffect(() => {
    if (workOrderSyncIntervalData?.workOrderSyncInterval) {
      setWorkOrderSyncInterval(workOrderSyncIntervalData.workOrderSyncInterval);
    }
  }, [workOrderSyncIntervalData]);
  
  // Update local state when work order auto sync enabled status is loaded
  useEffect(() => {
    if (workOrderAutoSyncEnabledData?.isWorkOrderAutoSyncEnabled !== undefined) {
      setWorkOrderAutoSyncEnabled(workOrderAutoSyncEnabledData.isWorkOrderAutoSyncEnabled);
    }
  }, [workOrderAutoSyncEnabledData]);

  useEffect(() => {
    if (reviewSyncSettingsData?.reviewSyncInterval) setReviewSyncInterval(reviewSyncSettingsData.reviewSyncInterval);
    if (reviewSyncSettingsData?.isReviewAutoSyncEnabled !== undefined) setReviewAutoSyncEnabled(reviewSyncSettingsData.isReviewAutoSyncEnabled);
  }, [reviewSyncSettingsData]);



  // Save walkthrough hours threshold
  const handleSaveWalkthroughHours = async () => {
    await updateWalkthroughHours({
      variables: {
        hours: parseInt(walkthroughHoursThreshold, 10)
      }
    });
  };

  // Save reservation sync interval
  const handleSaveReservationSyncInterval = async () => {
    await updateReservationSyncInterval({
      variables: {
        hours: parseInt(reservationSyncInterval, 10)
      }
    });
  };
  
  // Handle toggling auto sync
  const handleToggleAutoSync = async () => {
    const newState = !autoSyncEnabled;
    setAutoSyncEnabled(newState);
    
    try {
      await updateAutoSyncEnabled({
        variables: {
          enabled: newState
        }
      });
    } catch (error) {
      // The error handler in the mutation definition will revert the toggle if there's an error
      console.error('Error toggling auto sync:', error);
    }
  };
  
  // Save property sync interval
  const handleSavePropertySyncInterval = async () => {
    await updatePropertySyncInterval({
      variables: {
        hours: parseInt(propertySyncInterval, 10)
      }
    });
  };
  
  // Handle toggling property auto sync
  const handleTogglePropertyAutoSync = async () => {
    const newState = !propertyAutoSyncEnabled;
    setPropertyAutoSyncEnabled(newState);
    
    try {
      await updatePropertyAutoSyncEnabled({
        variables: {
          enabled: newState
        }
      });
    } catch (error) {
      // The error handler in the mutation definition will revert the toggle if there's an error
      console.error('Error toggling property auto sync:', error);
    }
  };
  
  // Save work order sync interval
  const handleSaveWorkOrderSyncInterval = async () => {
    await updateWorkOrderSyncInterval({
      variables: {
        minutes: parseInt(workOrderSyncInterval, 10)
      }
    });
  };
  
  // Handle toggling work order auto sync
  const handleToggleWorkOrderAutoSync = async () => {
    const newState = !workOrderAutoSyncEnabled;
    setWorkOrderAutoSyncEnabled(newState);
    
    try {
      await updateWorkOrderAutoSyncEnabled({
        variables: {
          enabled: newState
        }
      });
    } catch (error) {
      // The error handler in the mutation definition will revert the toggle if there's an error
      console.error('Error toggling work order auto sync:', error);
    }
  };

  const handleSaveReviewSyncInterval = async () => {
    await updateReviewSyncInterval({ variables: { hours: parseInt(reviewSyncInterval, 10) } });
  };

  const handleToggleReviewAutoSync = async () => {
    const enabled = !reviewAutoSyncEnabled;
    setReviewAutoSyncEnabled(enabled);
    await updateReviewAutoSyncEnabled({ variables: { enabled } });
    toast.success(`Automatic review syncing ${enabled ? 'enabled' : 'disabled'}`);
  };



  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Job Settings</h2>
      </div>

      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <h3 className="text-md font-medium text-gray-700 mb-2">Walkthrough Job Creation</h3>
          <p className="text-sm text-gray-500 mb-4">
            Configure when walkthrough jobs are created based on the time between reservations.
          </p>

          <div className="flex items-center mt-2">
            <label className="block text-sm font-medium text-gray-700 mr-4">
              Hours between checkout and next check-in:
            </label>
            
            {isEditingHours ? (
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="336" // 14 days
                  value={walkthroughHoursThreshold}
                  onChange={(e) => setWalkthroughHoursThreshold(e.target.value)}
                  className="shadow-sm focus:ring-sky-500 focus:border-sky-500 block w-24 sm:text-sm border-gray-300 rounded-md"
                />
                <button
                  onClick={handleSaveWalkthroughHours}
                  disabled={updatingHours}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  {updatingHours ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingHours(false);
                    if (hoursThresholdData?.walkthroughHoursThreshold) {
                      setWalkthroughHoursThreshold(hoursThresholdData.walkthroughHoursThreshold);
                    }
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-900 font-medium">
                  {hoursThresholdLoading ? 'Loading...' : `${walkthroughHoursThreshold} hours`}
                </span>
                <button
                  onClick={() => setIsEditingHours(true)}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Walkthrough jobs will only be created if the next reservation is at least this many hours after the current reservation's checkout.
            If there is no next reservation, a walkthrough job will always be created.
          </p>
        </div>

        {/* Property Sync Interval */}
        <div className="border-b border-gray-200 pb-4 mt-4">
          <h3 className="text-md font-medium text-gray-700 mb-2">Property Sync Interval</h3>
          <p className="text-sm text-gray-500 mb-4">
            Configure how frequently property data is automatically synchronized from Streamline.
          </p>

          {/* Auto-sync toggle */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Automatic Property Syncing
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Enable or disable automatic syncing of property data
              </p>
            </div>
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input
                type="checkbox"
                id="property-auto-sync-toggle"
                name="property-auto-sync-toggle"
                className="sr-only"
                checked={propertyAutoSyncEnabled}
                onChange={handleTogglePropertyAutoSync}
                disabled={updatingPropertyAutoSync}
              />
              <label
                htmlFor="property-auto-sync-toggle"
                className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                  propertyAutoSyncEnabled ? 'bg-sky-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${
                    propertyAutoSyncEnabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                ></span>
              </label>
            </div>
          </div>

          <div className="flex items-center mt-2">
            <label className="block text-sm font-medium text-gray-700 mr-4">
              Sync interval (hours):
            </label>
            
            {isEditingPropertySyncInterval ? (
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="168"
                  value={propertySyncInterval}
                  onChange={(e) => setPropertySyncInterval(e.target.value)}
                  className="shadow-sm focus:ring-sky-500 focus:border-sky-500 block w-24 sm:text-sm border-gray-300 rounded-md"
                />
                <button
                  onClick={handleSavePropertySyncInterval}
                  disabled={updatingPropertyInterval}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  {updatingPropertyInterval ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingPropertySyncInterval(false);
                    if (propertySyncIntervalData?.propertySyncInterval) {
                      setPropertySyncInterval(propertySyncIntervalData.propertySyncInterval);
                    }
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-900 font-medium">
                  {propertySyncIntervalLoading ? 'Loading...' : `${propertySyncInterval} hours`}
                </span>
                <button
                  onClick={() => setIsEditingPropertySyncInterval(true)}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  disabled={!propertyAutoSyncEnabled}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          
          {propertyLastSyncData?.propertySyncLastRun && (
            <div className="mt-2 text-sm text-gray-600">
              Last run: {new Date(propertyLastSyncData.propertySyncLastRun).toLocaleString()}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            {propertyAutoSyncEnabled 
              ? `Property data will be automatically synced from Streamline at this interval. Properties typically change less frequently than reservations, so a longer interval is recommended.`
              : `Automatic property syncing is currently disabled. You can still manually sync properties using the "Sync Now" button in the Data Synchronization section.`}
          </p>
        </div>

        {/* Work Order Sync Interval */}
        <div className="border-b border-gray-200 pb-4 mt-4">
          <h3 className="text-md font-medium text-gray-700 mb-2">Work Order Sync Interval</h3>
          <p className="text-sm text-gray-500 mb-4">
            Configure how frequently work order data is automatically synchronized from Streamline.
          </p>

          {/* Auto-sync toggle */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Automatic Work Order Syncing
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Enable or disable automatic syncing of work order data
              </p>
            </div>
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input
                type="checkbox"
                id="work-order-auto-sync-toggle"
                name="work-order-auto-sync-toggle"
                className="sr-only"
                checked={workOrderAutoSyncEnabled}
                onChange={handleToggleWorkOrderAutoSync}
                disabled={updatingWorkOrderAutoSync}
              />
              <label
                htmlFor="work-order-auto-sync-toggle"
                className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                  workOrderAutoSyncEnabled ? 'bg-sky-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${
                    workOrderAutoSyncEnabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                ></span>
              </label>
            </div>
          </div>

          <div className="flex items-center mt-2">
            <label className="block text-sm font-medium text-gray-700 mr-4">
              Sync interval (minutes):
            </label>
            
            {isEditingWorkOrderSyncInterval ? (
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="10"
                  max="1440"
                  value={workOrderSyncInterval}
                  onChange={(e) => setWorkOrderSyncInterval(e.target.value)}
                  className="shadow-sm focus:ring-sky-500 focus:border-sky-500 block w-24 sm:text-sm border-gray-300 rounded-md"
                />
                <button
                  onClick={handleSaveWorkOrderSyncInterval}
                  disabled={updatingWorkOrderInterval}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  {updatingWorkOrderInterval ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingWorkOrderSyncInterval(false);
                    if (workOrderSyncIntervalData?.workOrderSyncInterval) {
                      setWorkOrderSyncInterval(workOrderSyncIntervalData.workOrderSyncInterval);
                    }
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-900 font-medium">
                  {workOrderSyncIntervalLoading ? 'Loading...' : `${workOrderSyncInterval} minutes`}
                </span>
                <button
                  onClick={() => setIsEditingWorkOrderSyncInterval(true)}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  disabled={!workOrderAutoSyncEnabled}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          
          {workOrderLastSyncData?.workOrderSyncLastRun && (
            <div className="mt-2 text-sm text-gray-600">
              Last run: {new Date(workOrderLastSyncData.workOrderSyncLastRun).toLocaleString()}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            {workOrderAutoSyncEnabled 
              ? `Work order data will be automatically synced from Streamline at this interval. This includes maintenance requests, inspections, and other service orders. Valid range: 10-1440 minutes.`
              : `Automatic work order syncing is currently disabled. You can still manually sync work orders using the "Sync Now" button in the Data Synchronization section.`}
          </p>
        </div>

        {/* Reservation Sync Interval */}
        <div className="border-b border-gray-200 pb-4 mt-4">
          <h3 className="text-md font-medium text-gray-700 mb-2">Reservation Sync Interval</h3>
          <p className="text-sm text-gray-500 mb-4">
            Configure how frequently reservation data is automatically synchronized from Streamline.
          </p>

          {/* Auto-sync toggle */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Automatic Reservation Syncing
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Enable or disable automatic syncing of reservation data
              </p>
            </div>
            <div className="relative inline-block w-10 mr-2 align-middle select-none">
              <input
                type="checkbox"
                id="auto-sync-toggle"
                name="auto-sync-toggle"
                className="sr-only"
                checked={autoSyncEnabled}
                onChange={handleToggleAutoSync}
                disabled={updatingAutoSync}
              />
              <label
                htmlFor="auto-sync-toggle"
                className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                  autoSyncEnabled ? 'bg-sky-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`block h-6 w-6 rounded-full bg-white transform transition-transform duration-200 ease-in-out ${
                    autoSyncEnabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                ></span>
              </label>
            </div>
          </div>

          <div className="flex items-center mt-2">
            <label className="block text-sm font-medium text-gray-700 mr-4">
              Sync interval (hours):
            </label>
            
            {isEditingSyncInterval ? (
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={reservationSyncInterval}
                  onChange={(e) => setReservationSyncInterval(e.target.value)}
                  className="shadow-sm focus:ring-sky-500 focus:border-sky-500 block w-24 sm:text-sm border-gray-300 rounded-md"
                />
                <button
                  onClick={handleSaveReservationSyncInterval}
                  disabled={updatingInterval}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  {updatingInterval ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingSyncInterval(false);
                    if (syncIntervalData?.reservationSyncInterval) {
                      setReservationSyncInterval(syncIntervalData.reservationSyncInterval);
                    }
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-900 font-medium">
                  {syncIntervalLoading ? 'Loading...' : `${reservationSyncInterval} hours`}
                </span>
                <button
                  onClick={() => setIsEditingSyncInterval(true)}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  disabled={!autoSyncEnabled}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          
          {lastSyncData?.reservationSyncLastRun && (
            <div className="mt-2 text-sm text-gray-600">
              Last run: {new Date(lastSyncData.reservationSyncLastRun).toLocaleString()}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            {autoSyncEnabled 
              ? `Reservation data will be automatically synced from Streamline at this interval. A lower interval ensures more up-to-date data but may increase API usage.`
              : `Automatic reservation syncing is currently disabled. You can still manually sync reservations using the "Sync Now" button above.`}
          </p>
        </div>

        {/* Review Sync Interval */}
        <div className="border-b border-gray-200 pb-4 mt-4">
          <h3 className="text-md font-medium text-gray-700 mb-2">Review Sync Interval</h3>
          <p className="text-sm text-gray-500 mb-4">
            Configure how frequently recent guest reviews are synchronized from Streamline.
          </p>

          <div className="flex items-center justify-between mb-4">
            <div>
              <label htmlFor="review-auto-sync-toggle" className="text-sm font-medium text-gray-700">Automatic Review Syncing</label>
              <p className="text-xs text-gray-500 mt-1">The automatic run refreshes the most recent 31 days.</p>
            </div>
            <input
              id="review-auto-sync-toggle"
              type="checkbox"
              checked={reviewAutoSyncEnabled}
              onChange={handleToggleReviewAutoSync}
              disabled={updatingReviewAutoSync}
              className="h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="block text-sm font-medium text-gray-700">Sync interval (hours):</label>
            {isEditingReviewSyncInterval ? (
              <>
                <input
                  type="number"
                  min="1"
                  max="168"
                  value={reviewSyncInterval}
                  onChange={(event) => setReviewSyncInterval(event.target.value)}
                  className="shadow-sm focus:ring-sky-500 focus:border-sky-500 block w-24 sm:text-sm border-gray-300 rounded-md"
                />
                <button
                  onClick={handleSaveReviewSyncInterval}
                  disabled={updatingReviewInterval}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
                >
                  {updatingReviewInterval ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingReviewSyncInterval(false);
                    setReviewSyncInterval(reviewSyncSettingsData?.reviewSyncInterval || 24);
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span className="text-sm text-gray-900 font-medium">
                  {reviewSyncSettingsLoading ? 'Loading...' : `${reviewSyncInterval} hours`}
                </span>
                <button
                  onClick={() => setIsEditingReviewSyncInterval(true)}
                  disabled={!reviewAutoSyncEnabled}
                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Edit
                </button>
              </>
            )}
          </div>

          <div className="mt-3 space-y-1 text-sm text-gray-600">
            {reviewSyncSettingsData?.reviewSyncLastRun && <div>Last attempt: {new Date(reviewSyncSettingsData.reviewSyncLastRun).toLocaleString()}</div>}
            {reviewSyncSettingsData?.reviewSyncLastSuccess && <div>Last success: {new Date(reviewSyncSettingsData.reviewSyncLastSuccess).toLocaleString()}</div>}
            {reviewSyncSettingsData?.reviewSyncLastError && <div className="text-red-600">Last error: {reviewSyncSettingsData.reviewSyncLastError}</div>}
          </div>
          <p className="text-xs text-gray-500 mt-2">Valid interval: 1–168 hours. The default is once every 24 hours.</p>
        </div>


      </div>
    </div>
  );
}

export default JobSettingsSection;
